/**
 * Form03_ChronicCareManagement.jsx
 * Collection: chronic_care_management
 * Redirect: /forms/04-vestibular-consent
 */
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL = "/api/forms/chronic-care-management";
const NEXT    = "/forms/04-vestibular-consent";

export default function ChronicCareManagement({ patientName="", patientAge="", patientGender="", practiceName="", onSubmit }) {
  const history = useHistory();
  const formRef  = useRef(null);
  const sigRef   = useRef(null);
  const [sigEmpty, setSigEmpty]     = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savedId, setSavedId]       = useState(null);
  const [showToast, Toast]          = useToast();

  const [form, setForm] = useState({
    printName:        patientName,
    bestPhone:        "",
    legalRep:         "",
    legalRepSig:      "",   // text note; actual drawn sig captured below
    responsibleParty: patientName,
    relationship:     "Self",
    sigDate:          new Date().toISOString().split("T")[0],
  });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const getSig = () => (!sigRef.current||sigRef.current.isEmpty()) ? null : sigRef.current.toDataURL("image/png");

  const buildDoc = () => ({
    formType:    "chronic_care_management",
    patient:     { name: patientName, age: patientAge !== "" ? Number(patientAge) : null, gender: patientGender },
    practiceName,
    enrollment: {
      printName:   form.printName,
      bestPhone:   form.bestPhone,
      legalRep:    form.legalRep,
    },
    authorization: {
      responsibleParty: form.responsibleParty,
      relationship:     form.relationship,
      sigDate:          form.sigDate || null,
      signatureDataURL: getSig(),
    },
    meta: { submittedAt: new Date().toISOString(), formVersion:"1.0.0" },
  });

  const handleSubmit = async () => {
    if (sigEmpty) { showToast("Please sign before submitting.","error"); return; }
    setSubmitting(true);
    try {
      const doc = buildDoc();
      const res = typeof onSubmit==="function" ? await onSubmit(doc) : await saveToMongo(doc, API_URL);
      if (res?._id) setSavedId(res._id);
      showToast("Enrolled! Redirecting…","success");
      setTimeout(()=>history.push(NEXT),1800);
    } catch(e){ console.error(e); showToast("Save failed.","error"); }
    finally { setSubmitting(false); }
  };

  const handleDownload = async () => {
    try { await downloadPDF(formRef,`ccm-${patientName||"patient"}.pdf`); showToast("Downloaded!","success"); }
    catch { showToast("PDF failed.","error"); }
  };

  const practice = practiceName || "your practice";

  return (
    <FormShell title="Chronic Care Management Program"
      subtitle={`Thank you for choosing ${practice}. This program provides services to help you manage your chronic conditions.`}
      dbLabel="MongoDB · chronic_care_management"
      patientName={patientName} patientAge={patientAge} patientGender={patientGender}
      savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">ℹ</span>Program Benefits</div>
        <div className="wf-policy">
          <ul>
            <li>We'll create a Care Plan that is custom-designed to meet your goals. (You'll receive a copy.)</li>
            <li>We'll do a full assessment of your health needs.</li>
            <li>Every month, we'll check in to make sure things are going well.</li>
            <li>You'll have access to a healthcare provider who can address your chronic conditions 24/7.</li>
            <li>We'll aim to address issues outside the office, resulting in fewer office visits and fewer hospital admissions.</li>
          </ul>
        </div>
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">✓</span>Consent Points</div>
        <div className="wf-policy">
          <ul>
            <li>We may share your information with your other providers electronically to keep all involved with your care up-to-date.</li>
            <li>Only one provider can provide this service to you during a 30-day period.</li>
            <li>You agree that <strong>{practice}</strong> will be that one provider.</li>
            <li>You can cancel or revoke this service at any time by talking to our staff.</li>
            <li>Depending on your insurance, you may be billed for a small portion of this CCM service. In many cases the service is free; in some cases there is an $8/month copay. We'll let you know which applies to you.</li>
          </ul>
        </div>
      </div>

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">2</span>Enrollment Information</div>
        <div className="wf-grid wf-grid-2">
          <Field label="Print Name">
            <input type="text" value={form.printName} onChange={e=>set("printName",e.target.value)} placeholder="Full name" />
          </Field>
          <Field label="Best Phone Number">
            <input type="tel" value={form.bestPhone} onChange={e=>set("bestPhone",e.target.value)} placeholder="(___) ___-____" />
          </Field>
          <Field label="Legal Representative / Power of Attorney (Optional)">
            <input type="text" value={form.legalRep} onChange={e=>set("legalRep",e.target.value)} placeholder="Name if applicable" />
          </Field>
        </div>
      </div>

      <SigBlock sigRef={sigRef} sigEmpty={sigEmpty} setSigEmpty={setSigEmpty}
        responsibleParty={form.responsibleParty} setResponsibleParty={v=>set("responsibleParty",v)}
        relationship={form.relationship} setRelationship={v=>set("relationship",v)}
        sigDate={form.sigDate} setSigDate={v=>set("sigDate",v)} />

      {Toast}
    </FormShell>
  );
}

/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const CCMSchema = new Schema({
  formType:    { type:String, default:"chronic_care_management" },
  patient:     { name:String, age:Number, gender:String },
  practiceName: String,
  enrollment:  { printName:String, bestPhone:String, legalRep:String },
  authorization: { responsibleParty:String, relationship:String, sigDate:Date, signatureDataURL:String },
  meta: { submittedAt:Date, formVersion:String },
}, { timestamps:true, collection:"chronic_care_management" });
*/
